import { Directive, HostListener, Input, Optional, Self } from '@angular/core';
import { NgControl } from '@angular/forms';

/**
 * Numbers-only input guard for reactive-form text inputs.
 *
 * Usage: `<input pInputText appDigitsOnly="10" formControlName="mobileNumber">`
 *
 * - Blocks non-digit key presses.
 * - Strips non-digits on paste / programmatic input and enforces the max length.
 * - Writes the cleaned value back through the bound control so the form model
 *   and the DOM never drift.
 */
@Directive({
  selector: '[appDigitsOnly]',
  standalone: true,
})
export class DigitsOnlyDirective {
  /** Max number of digits to keep (the attribute value, e.g. "10"). */
  @Input() appDigitsOnly: number | string = '';

  constructor(@Optional() @Self() private ngControl: NgControl) {}

  @HostListener('keypress', ['$event'])
  onKeypress(e: KeyboardEvent): void {
    // Single printable char that isn't a digit -> block (lets through
    // Backspace/Tab/arrows whose key names are longer than one char).
    if (e.key && e.key.length === 1 && !/\d/.test(e.key)) {
      e.preventDefault();
    }
  }

  @HostListener('input', ['$event'])
  onInput(e: Event): void {
    const el = e.target as HTMLInputElement;
    const max = Number(this.appDigitsOnly) || 0;
    let digits = (el.value || '').replace(/\D+/g, '');
    if (max > 0) digits = digits.slice(0, max);
    if (digits !== el.value) {
      el.value = digits;
      // Keep the reactive control in sync (covers paste + blocked input).
      this.ngControl?.control?.setValue(digits, { emitEvent: true });
    }
  }
}
